/**
 * Terms of Service Page
 * ThePedigreeApp
 */

import { FileText, AlertTriangle, Scale, Ban, CreditCard, Shield } from 'lucide-react';
import { Card } from '../components/shared/UIComponents';

export const TermsPage = () => {
  const lastUpdated = "4 February 2025";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-feline-600 to-feline-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-feline-200" />
          <h1 className="text-4xl font-display font-bold mb-4">Terms of Service</h1>
          <p className="text-xl text-feline-100">Last updated: {lastUpdated}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="prose prose-lg max-w-none">

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-600">
              By accessing or using ThePedigreeApp ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Platform.
            </p>
            <p className="text-gray-600">
              These terms apply to all users, including breeders, buyers, and visitors.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-600">
              ThePedigreeApp is a platform that connects pedigree cat breeders with potential buyers in South Africa. We provide:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>A marketplace for listing pedigree cats and kittens</li>
              <li>Breeder verification services</li>
              <li>Cattery management tools</li>
              <li>Registry submission assistance</li>
              <li>Communication tools between breeders and buyers</li>
            </ul>
            <p className="text-gray-600 mt-4">
              ThePedigreeApp does not own, breed, sell, or take possession of any animals. We are a platform connecting parties only.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-feline-500" /> 3. User Accounts
            </h2>
            
            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">3.1 Registration</h3>
            <p className="text-gray-600">
              To use certain features, you must create an account. You agree to provide accurate, current, and complete information during registration.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">3.2 Account Security</h3>
            <p className="text-gray-600">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">3.3 Breeder Verification</h3>
            <p className="text-gray-600">
              Breeders must provide valid registry credentials (SACC, CASA, CFSA, or equivalent). We reserve the right to verify this information and reject or suspend accounts with false information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-feline-500" /> 4. Fees and Payments
            </h2>
            
            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">4.1 Commission Structure</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Sales Commission:</strong> 10% of the sale price when a kitten/cat sells through the platform</li>
              <li><strong>Registry Services:</strong> 10% fee for registry submission assistance</li>
              <li><strong>Listing:</strong> Free to list cats and kittens</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">4.2 Payment Terms</h3>
            <p className="text-gray-600">
              Commissions are invoiced monthly. Payment is due within 14 days of invoice. Late payments may result in account suspension.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">4.3 Transactions Between Users</h3>
            <p className="text-gray-600">
              All payments between breeders and buyers are handled directly between the parties. ThePedigreeApp does not process these payments and is not responsible for disputes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Ban className="w-6 h-6 text-feline-500" /> 5. Prohibited Conduct
            </h2>
            <p className="text-gray-600">You agree NOT to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Provide false or misleading information</li>
              <li>List non-pedigree cats as pedigree</li>
              <li>List cats you do not own or have authority to sell</li>
              <li>Engage in fraudulent activity or scams</li>
              <li>Harass, threaten, or abuse other users</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Circumvent platform fees or commissions</li>
              <li>Use the platform for any illegal purpose</li>
              <li>Scrape, copy, or reproduce platform content</li>
              <li>Interfere with the platform's operation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Listings and Content</h2>
            
            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">6.1 Accuracy</h3>
            <p className="text-gray-600">
              Breeders are responsible for ensuring all listing information is accurate, including breed, pedigree status, health information, and pricing.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">6.2 Images</h3>
            <p className="text-gray-600">
              You must only upload images that you own or have permission to use. Images must accurately represent the cat being listed.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">6.3 Content Rights</h3>
            <p className="text-gray-600">
              By posting content, you grant ThePedigreeApp a non-exclusive license to use, display, and distribute that content on the platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Animal Welfare</h2>
            <p className="text-gray-600">
              All users must comply with South African animal welfare laws and regulations. Breeders must:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Provide proper care for all animals</li>
              <li>Not sell kittens under 12 weeks of age</li>
              <li>Ensure animals are healthy and vaccinated before sale</li>
              <li>Provide accurate health and veterinary information</li>
              <li>Follow ethical breeding practices</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-feline-500" /> 8. Disclaimers
            </h2>
            
            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">8.1 Platform Role</h3>
            <p className="text-gray-600">
              ThePedigreeApp is a platform only. We do not guarantee the quality, health, or pedigree status of any animal listed. Buyers should conduct their own due diligence.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">8.2 No Warranty</h3>
            <p className="text-gray-600">
              The platform is provided "as is" without warranties of any kind, either express or implied.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">8.3 User Disputes</h3>
            <p className="text-gray-600">
              We are not responsible for disputes between users. While we may assist in mediation, resolution is ultimately between the parties involved.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Scale className="w-6 h-6 text-feline-500" /> 9. Limitation of Liability
            </h2>
            <p className="text-gray-600">
              To the maximum extent permitted by law, ThePedigreeApp shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or other intangible losses.
            </p>
            <p className="text-gray-600 mt-4">
              Our total liability shall not exceed the fees paid by you to us in the 12 months preceding the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Indemnification</h2>
            <p className="text-gray-600">
              You agree to indemnify and hold harmless ThePedigreeApp and its affiliates from any claims, damages, or expenses arising from your use of the platform or violation of these terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Termination</h2>
            <p className="text-gray-600">
              We may suspend or terminate your account at any time for violation of these terms or for any other reason at our discretion. Upon termination, your right to use the platform ceases immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-600">
              These terms are governed by the laws of the Republic of South Africa. Any disputes shall be subject to the exclusive jurisdiction of the South African courts.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Changes to Terms</h2>
            <p className="text-gray-600">
              We may modify these terms at any time. Continued use of the platform after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact</h2>
            <div className="bg-gray-50 rounded-xl p-6 mt-4">
              <p className="text-gray-700"><strong>ThePedigreeApp</strong></p>
              <p className="text-gray-600">Email: legal@thepedigreeapp.co.za</p>
              <p className="text-gray-600">Website: www.thepedigreeapp.co.za</p>
              <p className="text-gray-600">Location: Western Cape, South Africa</p>
            </div>
          </section>

        </Card>
      </div>
    </div>
  );
};

export default TermsPage;
